<template>
  <div class="tooltip" :style="{ maxWidth: `${maxWidth}px` }">
    <div
      class="tooltip__trigger"
      @mouseenter="openTooltipOnDesktop"
      @mouseleave="closeTooltipOnDesktop"
      @click="openTooltipOnMobile"
    >
      <slot name="trigger" />
    </div>

    <div
      v-if="showTooltip"
      class="tooltip__content"
      :style="tooltipStyles"
      @mouseenter="openTooltipOnDesktop"
      @mouseleave="closeTooltipOnDesktop"
    >
      <slot name="content" />
      <div
        v-if="showTooltipOnMobile"
        class="tooltip__content-close"
        @click="closeTooltip"
      >
        <VIcon>mdi-close</VIcon>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref, onMounted, onUnmounted, computed,
} from '@vue/composition-api';

export default {
  name: 'Tooltip',
  props: {
    maxWidth: {
      type: Number,
      default: 400,
    },
    position: {
      type: String,
      default: 'right',
      validator: val => ['top', 'bottom', 'left', 'right'].includes(val),
    },
    positionMob: {
      type: String,
      default: null,
      validator: val => ['top', 'bottom', 'left', 'right', null].includes(val),
    },
  },
  setup(props) {
    const showTooltip = ref(false);
    const isMobile = ref(false);

    const showTooltipOnMobile = computed(() => isMobile.value && showTooltip.value);

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    onMounted(() => {
      checkMobile();
      window.addEventListener('resize', checkMobile);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile);
    });

    const closeTooltip = () => {
      showTooltip.value = false;
    };

    const openTooltipOnDesktop = () => {
      if (!isMobile.value) {
        showTooltip.value = true;
      }
    };

    const closeTooltipOnDesktop = () => {
      if (!isMobile.value) {
        showTooltip.value = false;
      }
    };

    const openTooltipOnMobile = () => {
      if (isMobile.value) {
        showTooltip.value = !showTooltip.value;
      }
    };

    const tooltipStyles = computed(() => {
      const baseStyles = {
        maxWidth: `${props.maxWidth}px`,
        minWidth: `${props.maxWidth}px`,
      };

      const positionToUse = isMobile.value && props.positionMob ? props.positionMob : props.position;

      switch (positionToUse) {
        case 'top':
          return {
            ...baseStyles, bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '10px',
          };
        case 'bottom':
          return {
            ...baseStyles, top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '10px',
          };
        case 'left':
          return {
            ...baseStyles, right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '10px',
          };
        case 'right':
        default:
          return {
            ...baseStyles, left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '10px',
          };
      }
    });

    return {
      showTooltip,
      isMobile,
      closeTooltip,
      openTooltipOnMobile,
      showTooltipOnMobile,
      openTooltipOnDesktop,
      closeTooltipOnDesktop,
      tooltipStyles,
    };
  },
};
</script>

<style lang="scss" scoped>
.tooltip {
  position: relative;
  display: inline-block;

  &__trigger {
    cursor: pointer;
    width: 40px;
  }

  &__content {
    position: absolute;
    padding: 20px;
    z-index: 10;
    background-color: #fff;
    color: #212121;
    font-size: 14px;
    line-height: 140%;
    box-shadow: 0px 4px 40px 6px rgba(49, 57, 68, 0.15);
    border-radius: 6px;
    display: flex;

    @media screen and (max-width: 800px) {
      max-width: 300px !important;
      min-width: 300px !important;
    }
    @media screen and (max-width: 380px) {
      max-width: 250px !important;
      min-width: 250px !important;
    }

    @media screen and (max-width: 768px) {
      transform: translateY(0) !important;
    }

    &-close {
      width: fit-content;
      height: fit-content;
      cursor: pointer;
      margin-left: auto;
    }
  }
}
</style>
